import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Menu, User } from "lucide-react";

import logoSrc from "@/assets/lel-logo.png";
const logo = { url: logoSrc };
import { primaryNav } from "@/lib/site-nav";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export function SiteHeader({
  transparent = false,
  overlay = false,
  light = false,
}: {
  transparent?: boolean;
  overlay?: boolean;
  /** White nav/logo treatment for dark hero backdrops. */
  light?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const linkClass = light
    ? "whitespace-nowrap text-[0.8125rem] font-light text-white/90 transition-colors hover:text-white xl:text-sm"
    : "whitespace-nowrap text-[0.8125rem] font-light text-header-ink transition-colors hover:text-primary xl:text-sm";

  return (
    <header
      className={`w-full ${
        overlay ? "absolute left-0 right-0 top-0 z-30" : "sticky top-0 z-40"
      } ${transparent ? "bg-transparent" : "border-b border-primary/10 bg-header/95 backdrop-blur-sm"}`}
    >
      <div className="relative mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-6">
        <Link to="/" className="flex items-center" aria-label="Leadership Edge Live home">
          <img
            src={logo.url}
            alt="Leadership Edge Live"
            className={`h-9 w-auto ${light ? "brightness-0 invert" : ""}`}
          />
        </Link>

        <nav
          className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-5 lg:flex xl:gap-7"
          aria-label="Primary"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {primaryNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={linkClass}
              activeProps={{ className: "text-primary" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div
          className="hidden items-center gap-6 lg:flex xl:gap-8"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          <a
              href="https://lab.leadershipedge.live/"
              className={`${linkClass} inline-flex items-center gap-1.5`}
            >
              <User className="h-4 w-4" aria-hidden="true" />
              Sign in
            </a>

          <Link
            to="/purchase/"
            className="inline-flex items-center whitespace-nowrap rounded-[6px] bg-[#693D94] px-4 py-[9.5px] text-[0.8125rem] font-semibold text-white transition-transform hover:-translate-y-0.5 xl:text-sm"
          >
            Get Started
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className={`inline-flex h-11 w-11 items-center justify-center rounded-md lg:hidden ${light ? "text-white" : "text-header-ink"}`}
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full max-w-sm overflow-y-auto">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <nav
              className="mt-8 flex flex-col gap-1"
              aria-label="Mobile"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {primaryNav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-3 py-2.5 text-sm font-light text-foreground/80 hover:bg-lavender hover:text-primary"
                >
                  {item.label}
                </Link>
              ))}

              <Link
                to="/leadership-gaps-assessment/"
                onClick={() => setOpen(false)}
                className="mt-1 flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-primary hover:bg-lavender"
              >
                Leadership Gaps Assessment
                <span className="rounded-full bg-primary px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase leading-none tracking-wider text-primary-foreground">
                  Free
                </span>
              </Link>

              <a
                  href="https://lab.leadershipedge.live/"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-1.5 rounded-md px-3 py-2.5 text-sm font-light text-foreground/80 hover:bg-lavender hover:text-primary"
                >
                  <User className="h-4 w-4" aria-hidden="true" />
                  Sign in
                </a>

              <Link
                to="/purchase/"
                onClick={() => setOpen(false)}
                className="mt-3 inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-5 py-[11.4px] text-sm font-semibold text-white"
              >
                Get Started
              </Link>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
